import { Button } from '@trm/ui-kit';
import { cx } from '@trm/ui-kit/utils';
import { AxiosError } from 'axios';
import { Formik } from 'formik';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import topstepLogo from '~/assets/images/auth/topstep_logo_new.svg';
import SuccessTick from '~/assets/images/icons/success-tick.png';
import { BASE_PATH } from '~/config';
import { SetPasswordResponse } from '~/types/api';
import axios from '~/utils/axios';
import { EMAIL_REGEX } from '~/utils/email-validation';
import {
  SPECIAL_CHARS_REGEX,
  hasNoSpaces,
  isLowercaseChar,
  isNumber,
  isSpecialChar,
  isUppercaseChar,
  minLength12,
} from '~/utils/password-validation';

import { OnboardingButton } from '../OnboardingButton/OnboardingButton';
import { OnboardingInput } from '../OnboardingInput/OnboardingInput';

interface PasswordRequirement {
  label: string;
  validator: (password: string) => boolean;
}

const passwordRequirements: PasswordRequirement[] = [
  { label: 'At least 12 characters', validator: minLength12 },
  { label: 'At least 1 lowercase letter (a-z)', validator: isLowercaseChar },
  { label: 'At least 1 uppercase letter (A-Z)', validator: isUppercaseChar },
  { label: 'At least 1 number (0-9)', validator: isNumber },
  { label: 'At least 1 special character', validator: isSpecialChar },
  { label: 'No spaces', validator: hasNoSpaces },
];

// Validation schema for requesting new link
const requestNewLinkValidationSchema = Yup.object().shape({
  email: Yup.string()
    .required('Email is required')
    .matches(EMAIL_REGEX, 'Must be a valid email address'),
});

// Validation schema for setting password
const setPasswordValidationSchema = Yup.object().shape({
  email: Yup.string()
    .required('Email is required')
    .matches(EMAIL_REGEX, 'Must be a valid email address'),
  password: Yup.string()
    .required('Password is required')
    .min(12, 'Password must be at least 12 characters')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(SPECIAL_CHARS_REGEX, 'Password must contain at least one special character')
    .matches(/^\S*$/, 'Password cannot contain spaces'),
  confirm: Yup.string()
    .required('Confirm Password is required')
    .test('confirm', "Passwords don't match.", function (this: Yup.TestContext, confirm?: string) {
      return this.parent.password === confirm;
    }),
});

const PasswordRequirementItem = ({ label, isValid }: { label: string; isValid: boolean }) => (
  <li className="flex items-center gap-2">
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      role="img"
      aria-label={isValid ? 'Requirement met' : 'Requirement not met'}
      className={cx('size-5', isValid ? 'text-green-600' : 'text-gray-500')}
    >
      <path
        d="M0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10Z"
        fill="currentColor"
      />
      <path
        d="M6.25 10L8.75 12.5L13.75 7.5"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    <span className="text-sm text-gray-400">{label}</span>
  </li>
);

// Success content component for password set
const SuccessContent = () => {
  return (
    <>
      {/* Icon */}
      <div className="mb-4 flex justify-center">
        <img src={SuccessTick} alt="Success checkmark" />
      </div>

      {/* Header */}
      <div className="mb-10 text-center">
        <h1 className="mb-2 text-display-xs font-semibold text-white">Success</h1>
        <p className="text-md text-gray-400">
          Your account is now secured with enhanced protection. You'll be prompted for an email
          verification code on your next login.
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-6 text-center">
        <OnboardingButton
          onClick={() => {
            window.location.href = `${BASE_PATH}/dashboard/default`;
          }}
        >
          Continue to Dashboard
        </OnboardingButton>
      </div>
    </>
  );
};

// Neutral success content for resend flow (does not disclose account existence)
const ResendSuccessContent = () => {
  return (
    <>
      {/* Icon */}
      <div className="mb-4 flex justify-center">
        <img src={SuccessTick} alt="Success checkmark" />
      </div>

      {/* Header */}
      <div className="mb-10 text-center">
        <h1 className="mb-2 text-display-xs font-semibold text-white">Link Sent</h1>
        <p className="text-md text-gray-400">
          If an account exists with that email, you'll receive a password setup link shortly. Please
          check your inbox and follow the instructions.
        </p>
      </div>
    </>
  );
};

// Invalid link content component
const InvalidLinkContent = ({
  emailFromUrl,
  handleRequestNewLink,
}: {
  emailFromUrl: string | null;
  handleRequestNewLink: (email: string) => Promise<void>;
}) => (
  <Formik
    initialValues={{ email: emailFromUrl || '' }}
    validationSchema={requestNewLinkValidationSchema}
    onSubmit={async (values, { setStatus, setSubmitting }) => {
      try {
        await handleRequestNewLink(values.email);
      } catch (error: unknown) {
        if (error && error instanceof AxiosError) {
          setStatus({
            success: false,
            message: error.response?.data?.message || 'Failed to send new link. Please try again.',
          });
        } else if (error instanceof Error) {
          setStatus({
            success: false,
            message: error.message,
          });
        } else {
          setStatus({
            success: false,
            message: 'An unexpected error occurred. Please try again.',
          });
        }
        setSubmitting(false);
      }
    }}
  >
    {({
      errors,
      handleBlur,
      handleSubmit,
      isSubmitting,
      setFieldValue,
      status,
      touched,
      values,
    }) => (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="mb-6 text-center">
          <h1 className="mb-2 text-display-xs font-semibold text-white">Invalid or Expired Link</h1>
          <p className="text-md text-gray-400">
            This link is missing the verification code. Please check your email for the correct link
            or request a new password setup link.
          </p>
        </div>
        <div>
          <OnboardingInput
            label="Email"
            type="email"
            name="email"
            value={values.email}
            onChange={(value) => setFieldValue('email', value)}
            onBlur={handleBlur}
            isInvalid={Boolean(touched.email && errors.email)}
            error={touched.email && errors.email ? errors.email : undefined}
            isDisabled={!!emailFromUrl}
          />
        </div>
        {status?.message && <p className="text-sm text-red-600">{status.message}</p>}
        <OnboardingButton
          type="submit"
          isDisabled={isSubmitting || !values.email || !!errors.email}
          isLoading={isSubmitting}
        >
          {isSubmitting ? 'Sending...' : 'Send New Link'}
        </OnboardingButton>
      </form>
    )}
  </Formik>
);

// Set password form content component
const SetPasswordFormContent = ({
  emailFromUrl,
  code,
  showPassword,
  showConfirmPassword,
  handleClickShowPassword,
  handleClickShowConfirmPassword,
  setSuccess,
  setSuccessType,
  navigate,
}: {
  emailFromUrl: string | null;
  code: string;
  showPassword: boolean;
  showConfirmPassword: boolean;
  handleClickShowPassword: () => void;
  handleClickShowConfirmPassword: () => void;
  setSuccess: (value: boolean) => void;
  setSuccessType: (value: 'password-set' | 'resend') => void;
  navigate: ReturnType<typeof useNavigate>;
}) => (
  <>
    {/* Logo */}
    <div className="mb-10 flex justify-center">
      <img src={topstepLogo} alt="Topstep logo" className="h-10 w-auto" />
    </div>

    {/* Header */}
    <div className="mb-10 text-center">
      <h1 className="mb-1 text-display-xs font-semibold text-white">Set Password</h1>
      <p className="text-sm text-gray-400">Password must meet all eligibility criteria</p>
    </div>

    <Formik
      initialValues={{
        email: emailFromUrl || '',
        password: '',
        confirm: '',
        submit: null,
      }}
      validationSchema={setPasswordValidationSchema}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          const response = await axios.post<SetPasswordResponse>('/auth/cognito/set-password', {
            email: values.email,
            code,
            password: values.password,
          });

          if (response.data.success) {
            setStatus({ success: true });
            setSuccessType('password-set');
            setSuccess(true);
          } else {
            setErrors({
              submit: response.data.message || 'Failed to set password. Please try again.',
            });
            setStatus({ success: false });
            setSubmitting(false);
          }
        } catch (error: unknown) {
          if (error && error instanceof AxiosError) {
            const errorMessage =
              error.response?.data?.message ||
              error.response?.data?.error ||
              'An error occurred while setting your password. Please try again.';

            setErrors({
              submit: errorMessage,
            });
          } else {
            setErrors({ submit: 'An unexpected error occurred. Please try again.' });
          }
          setStatus({ success: false });
          setSubmitting(false);
        }
      }}
    >
      {({ errors, handleBlur, handleSubmit, isSubmitting, setFieldValue, touched, values }) => (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <OnboardingInput
              id="email-input"
              label="Email"
              placeholder="Email Address"
              type="email"
              name="email"
              value={values.email}
              onChange={(value) => setFieldValue('email', value)}
              onBlur={handleBlur}
              isRequired
              isDisabled={!!emailFromUrl}
              isInvalid={Boolean(touched.email && errors.email)}
              error={touched.email && errors.email ? errors.email : undefined}
              size="md"
            />
          </div>

          <div>
            <OnboardingInput
              id="password-input"
              label="New Password"
              placeholder="New Password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={values.password}
              onChange={(value) => setFieldValue('password', value)}
              onBlur={handleBlur}
              isInvalid={Boolean(touched.password && errors.password)}
              error={touched.password && errors.password ? errors.password : undefined}
              showPasswordToggle
              showPassword={showPassword}
              onTogglePassword={handleClickShowPassword}
              size="md"
            />
          </div>

          <div>
            <OnboardingInput
              id="confirm-password-input"
              label="Confirm New Password"
              placeholder="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirm"
              value={values.confirm}
              onChange={(value) => setFieldValue('confirm', value)}
              onBlur={handleBlur}
              isInvalid={Boolean(touched.confirm && errors.confirm)}
              error={touched.confirm && errors.confirm ? errors.confirm : undefined}
              showPasswordToggle
              showPassword={showConfirmPassword}
              onTogglePassword={handleClickShowConfirmPassword}
              size="md"
            />
          </div>

          {errors.submit && <p className="text-sm font-medium text-red-600">{errors.submit}</p>}

          <ul className="mt-6 mb-12 space-y-2">
            {passwordRequirements.map((req) => (
              <PasswordRequirementItem
                key={req.label}
                label={req.label}
                isValid={req.validator(values.password)}
              />
            ))}
          </ul>

          <div className="flex flex-col gap-3">
            <OnboardingButton
              type="submit"
              isDisabled={
                isSubmitting ||
                !(
                  minLength12(values.password) &&
                  isLowercaseChar(values.password) &&
                  isUppercaseChar(values.password) &&
                  isNumber(values.password) &&
                  isSpecialChar(values.password) &&
                  hasNoSpaces(values.password) &&
                  values.password === values.confirm
                )
              }
              isLoading={isSubmitting}
            >
              {isSubmitting ? 'Setting Password...' : 'Continue'}
            </OnboardingButton>
            <Button
              type="button"
              color="tertiary"
              onClick={() => {
                navigate('/login');
              }}
              className="self-center p-4 text-sm font-medium text-white"
            >
              Return to Topstep Login
            </Button>
          </div>
        </form>
      )}
    </Formik>
  </>
);

/**
 * Cognito password setup flow with success, invalid-link, and set-password states.
 *
 * Business rules:
 * - Uses centralized password validation rules.
 * - Invalid/expired links trigger resend flow without user-existence disclosure.
 *
 * `@example`
 * <SetCognitoPassword />
 *
 * `@see` app/src/pages/auth/onboarding/routes/EmailVerificationRoute.tsx
 */
export const SetCognitoPassword = () => {
  const [success, setSuccess] = useState(false);
  const [successType, setSuccessType] = useState<'password-set' | 'resend'>('password-set');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const code = searchParams.get('code');
  const emailParam = searchParams.get('email');
  const emailFromUrl = emailParam ? atob(emailParam) : null;

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleRequestNewLink = async (email: string) => {
    const response = await axios.post('/auth/cognito/request-password-setup', { email });
    if (response.data.success) {
      setSuccessType('resend');
      setSuccess(true);
    } else {
      throw new Error(response.data.message || 'Failed to send new link');
    }
  };

  // Determine which content to render
  let content;

  if (success) {
    content = successType === 'resend' ? <ResendSuccessContent /> : <SuccessContent />;
  } else if (!code) {
    content = (
      <InvalidLinkContent emailFromUrl={emailFromUrl} handleRequestNewLink={handleRequestNewLink} />
    );
  } else {
    content = (
      <SetPasswordFormContent
        emailFromUrl={emailFromUrl}
        code={code}
        showPassword={showPassword}
        showConfirmPassword={showConfirmPassword}
        handleClickShowPassword={handleClickShowPassword}
        handleClickShowConfirmPassword={handleClickShowConfirmPassword}
        setSuccess={setSuccess}
        setSuccessType={setSuccessType}
        navigate={navigate}
      />
    );
  }

  return (
    <div className="flex min-h-screen justify-center bg-gradient-brand-primary sm:items-center">
      <div className="w-full max-w-[480px] px-4 py-12 sm:border sm:border-primary sm:bg-primary sm:p-12">
        {content}
      </div>
    </div>
  );
};
